import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Shared shell for elementary mission pages.
 *
 * Provides consistent header, breadcrumb-style back-to-dashboard, lesson
 * panel, activity slot, and a primary "Save & Continue" button.
 */
export default function ElementaryMissionPage({
  missionNumber,
  title,
  lesson,
  children,            // activity content
  canContinue = true,
  onContinue,
  continueLabel = "Save & Continue",
  testId,
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-amber-50 to-white">
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/elementary/demo")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            data-testid="mission-back-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to CEO City
          </button>
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
            Mission {missionNumber} of 9
          </span>
        </div>
      </nav>

      <main className="pt-28 pb-16 px-4" data-testid={testId}>
        <div className="max-w-3xl mx-auto">
          {/* Mission header */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-7 sm:p-9 shadow-xl text-white mb-8">
            <p className="text-yellow-50 font-bold uppercase tracking-wide text-sm mb-2">
              Mission {missionNumber}
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{title}</h1>
          </div>

          {/* Lesson */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 ring-1 ring-yellow-100 shadow-sm mb-6">
            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-3">
              Lesson
            </p>
            <p className="text-gray-800 text-lg leading-relaxed">{lesson}</p>
          </div>

          {/* Activity */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 ring-1 ring-yellow-100 shadow-sm mb-8">
            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-4">
              Your Turn
            </p>
            {children}
          </div>

          {/* Continue */}
          <div className="flex justify-end">
            <button
              onClick={onContinue}
              disabled={!canContinue}
              className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg flex items-center gap-2"
              data-testid="mission-continue"
            >
              <CheckCircle2 className="w-5 h-5" />
              {continueLabel}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
